/* Card "Leve 3, Pague 2": atualização em tempo real e animações (especificação: docs/promo-leve3pague2.md).
   Só anima o que mudou de verdade no carrinho. Abrir o drawer ou recarregar a página sem mudança mostra o estado final.
   Se a mudança acontece com o carrinho lateral fechado ou ainda deslizando, o card fica no estado anterior
   e a animação toca quando o drawer termina de abrir (dialog:after-show), para o cliente ver. */
(() => {
  if (window.customElements.get('l3p2-card')) return;

  const LINES_KEY = 'l3p2-lines';
  const CHANGE_WINDOW = 2500; // ms após um cart:change em que mudanças nas linhas contam como reais
  const START_DELAY = 120; // respiro depois que o drawer termina de abrir
  const STEP_DELAY = 180; // intervalo entre espaços preenchidos em sequência
  const ACTIVATE_DELAY = 260; // atraso do espaço grátis depois que o card troca para o visual ativado
  let lastCartChange = 0;

  const readJSON = (key) => {
    try {
      return JSON.parse(sessionStorage.getItem(key));
    } catch (error) {
      return null;
    }
  };
  const writeJSON = (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* sessionStorage indisponível: só perde as animações */
    }
  };
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animationsEnabled = () => {
    const card = document.querySelector('l3p2-card');
    return Boolean(card) && card.dataset.animations === 'true' && !reducedMotion();
  };
  const play = (element, className) => {
    if (!element) return;
    element.classList.remove('l3p2-pre', className);
    void element.offsetWidth;
    element.classList.add(className);
    element.addEventListener('animationend', () => element.classList.remove(className), { once: true });
  };

  // Drawer totalmente aberto = recebeu dialog:after-show e ainda tem o atributo open
  const shownDrawers = new WeakSet();
  const pendingByDrawer = new WeakMap();
  const isDrawerVisible = (drawer) => shownDrawers.has(drawer) && drawer.hasAttribute('open');
  const whenDrawerVisible = (drawer, callback) => {
    if (!drawer || isDrawerVisible(drawer)) {
      callback();
      return;
    }
    if (!pendingByDrawer.has(drawer)) pendingByDrawer.set(drawer, []);
    pendingByDrawer.get(drawer).push(callback);
  };
  document.addEventListener('dialog:after-show', (event) => {
    const drawer = event.target;
    if (!(drawer instanceof HTMLElement) || drawer.tagName !== 'CART-DRAWER') return;
    shownDrawers.add(drawer);
    const queue = pendingByDrawer.get(drawer) || [];
    pendingByDrawer.delete(drawer);
    setTimeout(() => queue.forEach((callback) => callback()), START_DELAY);
  });
  document.addEventListener('dialog:after-hide', (event) => {
    if (event.target instanceof HTMLElement) shownDrawers.delete(event.target);
  });

  class L3P2Card extends HTMLElement {
    connectedCallback() {
      this._onCartChange = (event) => this.refresh(event);
      document.addEventListener('cart:change', this._onCartChange);
      document.addEventListener('cart:refresh', this._onCartChange);
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCartChange);
      document.removeEventListener('cart:refresh', this._onCartChange);
    }

    get stateKey() {
      return `l3p2-state:${this.closest('cart-drawer') ? 'drawer' : 'page'}`;
    }

    get canAnimate() {
      return this.dataset.animations === 'true' && !reducedMotion();
    }

    // Compara com o render anterior e anima só os elementos novos
    sync() {
      const token = (this._token = (this._token || 0) + 1);
      const previous = readJSON(this.stateKey);
      const current = {
        total: Number(this.dataset.total),
        filled: Number(this.dataset.filled),
        on: this.dataset.on === 'true'
      };
      writeJSON(this.stateKey, current);
      if (!previous || previous.total === current.total || !this.canAnimate) return;

      const combo = Number(this.dataset.combo);
      const fill = this.querySelector('.l3p2__bar-fill');
      const growing = current.total > previous.total;
      // Mais espaços cheios que antes: anima só os novos. Menos, mas o total cresceu: recomeçou o combo (modo repetir).
      const filledBefore = current.filled >= previous.filled ? previous.filled : 0;
      const newSlots = growing
        ? Array.from(this.querySelectorAll('.l3p2__slot.is-filled'))
            .filter((slot) => Number(slot.dataset.slot) > filledBefore)
            .map((slot) => slot.querySelector('.l3p2__slot-box'))
        : [];
      const turningOn = current.on && !previous.on;
      const drawer = this.closest('cart-drawer');
      const deferred = Boolean(drawer) && !isDrawerVisible(drawer);

      // Estado "antes": barra na largura anterior, espaços novos escondidos, card ainda bloqueado
      if (fill) {
        // Bolinhas: a linha vai do centro da 1ª ao da última; Espaços: a barra divide em N partes
        const previousWidth = this.dataset.layout === 'steps'
          ? (Math.max(previous.filled - 1, 0) / Math.max(combo - 1, 1)) * 100
          : (previous.filled / combo) * 100;
        fill.style.transition = 'none';
        fill.style.width = `${previousWidth}%`;
        void fill.offsetWidth;
      }
      if (deferred) {
        newSlots.forEach((box) => box.classList.add('l3p2-pre'));
        if (turningOn) this.classList.remove('is-on');
      }

      const run = () => {
        if (token !== this._token) return; // o card mudou de novo antes de tocar
        if (turningOn) this.classList.add('is-on');
        if (fill) {
          fill.style.transition = '';
          fill.style.width = `${this.dataset.progress}%`;
        }
        newSlots.forEach((box, index) => {
          const isGift = box.closest('.l3p2__slot--gift');
          const delay = isGift && turningOn ? Math.max(index * STEP_DELAY, ACTIVATE_DELAY) : index * STEP_DELAY;
          setTimeout(() => {
            if (token === this._token) play(box, 'l3p2-pop');
          }, delay);
        });
      };

      if (deferred) {
        whenDrawerVisible(drawer, run);
      } else {
        requestAnimationFrame(run);
      }
    }

    async refresh(event) {
      try {
        let html = null;
        const sections = event && event.detail && event.detail.cart ? event.detail.cart.sections : null;
        const section = this.closest('.shopify-section');
        const sectionId = section ? section.id.replace('shopify-section-', '') : null;
        if (sections && sectionId && sections[sectionId]) {
          html = sections[sectionId];
        } else {
          const response = await fetch(`${window.Shopify.routes.root}cart?view=l3p2`, { cache: 'no-store' });
          html = await response.text();
        }

        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('l3p2-card');
        if (!fresh) {
          this.hidden = true;
          return;
        }
        this.getAttributeNames().forEach((name) => {
          if (!fresh.hasAttribute(name)) this.removeAttribute(name);
        });
        Array.from(fresh.attributes).forEach((attribute) => this.setAttribute(attribute.name, attribute.value));
        this.innerHTML = fresh.innerHTML;
        this.sync();
      } catch (error) {
        console.error('l3p2-card: falha ao atualizar', error);
      }
    }
  }

  window.customElements.define('l3p2-card', L3P2Card);

  // Carrinho esvaziado: o drawer troca tudo pelo estado vazio, então zeramos a memória aqui
  document.addEventListener('cart:change', (event) => {
    lastCartChange = Date.now();
    const cart = event.detail && event.detail.cart;
    if (cart && cart.item_count === 0) {
      const empty = { total: 0, filled: 0, on: false };
      writeJSON('l3p2-state:drawer', empty);
      writeJSON('l3p2-state:page', empty);
    }
  });

  // Linhas do carrinho lateral: produto novo (rise), selo GRÁTIS (pop) e linha de desconto no resumo (rise)
  const watchDrawerLines = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const scan = (animate) => {
      const lines = Array.from(drawer.querySelectorAll('[data-l3p2-line]'));
      const badges = Array.from(drawer.querySelectorAll('[data-l3p2-badge]'));
      const discountLine = drawer.querySelector('[data-l3p2-discount-line]');
      const previous = readJSON(LINES_KEY);
      writeJSON(LINES_KEY, {
        keys: lines.map((line) => line.dataset.l3p2Line),
        badges: badges.map((badge) => badge.dataset.l3p2Badge),
        discount: Boolean(discountLine)
      });
      if (!animate || !previous || !animationsEnabled()) return;

      const toAnimate = [];
      lines.forEach((line) => {
        if (!previous.keys.includes(line.dataset.l3p2Line)) toAnimate.push([line, 'l3p2-rise']);
      });
      badges.forEach((badge) => {
        if (!previous.badges.includes(badge.dataset.l3p2Badge)) toAnimate.push([badge, 'l3p2-pop']);
      });
      if (discountLine && !previous.discount) toAnimate.push([discountLine, 'l3p2-rise']);
      if (!toAnimate.length) return;

      if (!isDrawerVisible(drawer)) toAnimate.forEach(([element]) => element.classList.add('l3p2-pre'));
      whenDrawerVisible(drawer, () => toAnimate.forEach(([element, className]) => play(element, className)));
    };

    scan(false);
    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        scan(Date.now() - lastCartChange < CHANGE_WINDOW);
      });
    }).observe(drawer, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawerLines);
  } else {
    watchDrawerLines();
  }
})();
